/**
 * Human approval gates from orchestrator SSE (approval_required).
 * Decisions are sent to core-api → llm-service resume only (no separate approval_requests row).
 *
 * mdm_mapping gates share their confirm rule with MaestroMappingsStepper via
 * `mappingApprovalGuard` — Approve stays disabled until there is something to
 * confirm (ORCH-373). Since ORCH-376 that is the dropdown's choice or a value
 * the user typed on the plan form, and Approve is what writes it into the plan:
 * Maestro's proposal is displayed but never applied on its own.
 *
 * user_input questions follow the same rule through `canSubmitUserInput`
 * (ORCH-373 Biz Review): Submit is disabled until the answer — typed text, a
 * date, or the number the shared renderer handed back — is non-empty, so an
 * empty Budget can't be submitted and no "enter a value" message is needed.
 *
 * Skip / Reject is a single action (ORCH-372): it resumes the orchestrator with
 * `rejected` straight away, with no intermediate "Continue" step. Skip is a
 * `SecondaryButton` and Reject a `DestructiveButton` — plain text, no border at
 * rest (ORCH-424 hierarchy, applied here by ORCH-426).
 *
 * A `user_input` question about a Create Plan field no longer lands here in plan
 * mode: `MaestroChatPanel` routes it to `MaestroMappingsStepper`, where it is the
 * card of the field it is about (ORCH-426 Biz Review). This card keeps the
 * fieldless questions (a free-form clarification) and review gates.
 *
 * A numeric `user_input` question (Maestro asking for the Budget) is answered
 * through `ApprovalNumberField`, which renders the plan form's own Budget field
 * via the shared `CurrencyFieldRenderer` — grouped as the user types, plain
 * number underneath (ORCH-374). The card never keeps a parallel number input.
 */

import React from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import type { ApprovalRequiredEvent } from '../../types/chat';
import { brandColors } from '../../theme/orchestrateTheme';
import ApprovalMappingField from './ApprovalMappingField';
import { wrapForFieldShape } from './createPlanFieldValues';
import ApprovalNumberField, { PLAN_CURRENCY_FD_KEY } from './ApprovalNumberField';
import { canConfirmMapping, canSubmitUserInput, isEmptyFieldValue } from './mappingApprovalGuard';
import { useCreatePlanFieldLabels } from '../../hooks/useModelFieldLabels';
import { maestroSurfaceSx } from './maestroSurface';
import { DestructiveButton, SecondaryButton } from '../common';

export interface ApprovalQueueItem {
  key: string;
  data: ApprovalRequiredEvent;
}

export type OrchestratorResumeDetail = {
  decision: 'approved' | 'rejected';
  comments?: string;
  /** Present when the user approves an mdm_mapping gate with a chosen option. */
  mapping?: {
    fd_key: string;
    field_label?: string;
    choice_label: string;
    choice_value: unknown;
  };
  /** user_input mid-run gate */
  user_input?: string;
};

export interface ApprovalCardProps {
  items: ApprovalQueueItem[];
  runId: string | null;
  sessionId: string | null;
  disabled?: boolean;
  onResolved: (key: string) => void;
  onError: (message: string) => void;
  /** MDM list field — same tenant as Create Media Plan. */
  tenantId: string;
  /** Read the current Create Media Plan field value (metadata[fdKey]) for live sync. */
  getFormFieldValue: (fdKey: string) => unknown;
  /** Apply list / field values to the plan form (TemplateForm ref). */
  onApplyCreatePlanFields?: (fields: Record<string, unknown>) => void;
  onOrchestratorResume?: (detail: OrchestratorResumeDetail) => void | Promise<void>;
}

const ApprovalCard: React.FC<ApprovalCardProps> = ({
  items,
  runId,
  sessionId,
  disabled,
  onResolved,
  onError,
  tenantId,
  getFormFieldValue,
  onApplyCreatePlanFields,
  onOrchestratorResume,
}) => {
  const [busyKey, setBusyKey] = React.useState<string | null>(null);
  const [comments, setComments] = React.useState<Record<string, string>>({});
  const [userInputValues, setUserInputValues] = React.useState<Record<string, string>>({});
  /** Numeric answers — kept as numbers so the mask's display text never reaches the API (ORCH-374). */
  const [userInputNumbers, setUserInputNumbers] = React.useState<Record<string, number | null>>({});
  /** Latest label/value from mapping dropdown (for resume). */
  const [mappingChoice, setMappingChoice] = React.useState<
    Record<string, { label: string; value: unknown } | null>
  >({});
  const [mappingHint, setMappingHint] = React.useState<Record<string, string>>({});
  /** ORCH-381: card headers read the Create Media Plan form's own metadata labels. */
  const { labelFor } = useCreatePlanFieldLabels(tenantId);

  if (!items.length) return null;

  const isNumberInput = (ev: ApprovalRequiredEvent) =>
    ev.approval_phase === 'user_input' && ev.user_input?.field_type === 'number';

  /**
   * The answer a user_input question would submit right now — the plain number
   * for a numeric question (null until typed), else the typed text. Both the
   * Submit button's enabled state and the click handler read this one value, so
   * they can never disagree (ORCH-373).
   */
  const userInputAnswer = (key: string, ev: ApprovalRequiredEvent): unknown =>
    isNumberInput(ev) ? (userInputNumbers[key] ?? null) : (userInputValues[key] ?? '');

  const titleFor = (ev: ApprovalRequiredEvent) => {
    if (ev.approval_phase === 'user_input' && ev.user_input)
      return ev.user_input.field || 'Input required';
    if (ev.approval_phase === 'mdm_mapping' && ev.mapping?.fd_key) return 'Confirm mapping';
    return 'Approval required';
  };

  const handleDecision = async (
    key: string,
    ev: ApprovalRequiredEvent,
    decision: 'approved' | 'rejected'
  ) => {
    if (ev.approval_phase === 'user_input' && decision === 'approved') {
      // Submit is disabled while the answer is empty (ORCH-373), so this only
      // guards the window between a render and a click — silently, since the
      // user never had an enabled button to press.
      if (!canSubmitUserInput(userInputAnswer(key, ev))) return;
    }
    if (ev.approval_phase === 'mdm_mapping' && ev.mapping && decision === 'approved') {
      // ORCH-376: the plan form is no longer seeded with Maestro's proposal, so
      // an empty form value is normal here — validate the value that would
      // actually be sent (the dropdown's choice, else the form's own value).
      const v = getFormFieldValue(ev.mapping.fd_key);
      const mc = mappingChoice[key];
      const choice_value = mc?.value !== undefined ? mc.value : v;
      if (isEmptyFieldValue(choice_value)) {
        setMappingHint((h) => ({ ...h, [key]: 'Choose a value before approving.' }));
        return;
      }
      setMappingHint((h) => {
        const n = { ...h };
        delete n[key];
        return n;
      });
    }

    if (!runId || !sessionId) {
      onError(
        'Missing orchestrator thread or session — cannot resume. Try sending another message.'
      );
      return;
    }
    setBusyKey(key);
    try {
      const detail: OrchestratorResumeDetail = {
        decision,
        comments: comments[key]?.trim() || undefined,
      };

      if (decision === 'approved' && ev.approval_phase === 'mdm_mapping' && ev.mapping) {
        const fd = ev.mapping.fd_key;
        const v = getFormFieldValue(fd);
        const mc = mappingChoice[key];
        const choiceValue = mc?.value !== undefined ? mc.value : v;
        const choiceScalar = Array.isArray(choiceValue) ? choiceValue[0] : choiceValue;
        detail.mapping = {
          fd_key: fd,
          field_label: ev.mapping.field_label,
          choice_label:
            mc?.label ||
            (typeof ev.mapping.proposed_label === 'string' ? ev.mapping.proposed_label : '') ||
            String(choiceScalar ?? ''),
          choice_value: choiceScalar,
        };
        // Approve is the explicit user action that writes the value into the
        // plan — nothing put it there earlier (ORCH-376).
        onApplyCreatePlanFields?.({ [fd]: wrapForFieldShape(fd, choiceValue) });
      }

      if (decision === 'approved' && ev.approval_phase === 'user_input' && ev.user_input?.fd_key) {
        if (isNumberInput(ev)) {
          // The answer is the plain number the renderer handed back — never the
          // grouped display text (ORCH-374).
          const amount = userInputNumbers[key];
          if (typeof amount === 'number') {
            detail.user_input = String(amount);
            onApplyCreatePlanFields?.({ [ev.user_input.fd_key]: amount });
          }
        } else {
          const trimmed = userInputValues[key]?.trim() ?? '';
          detail.user_input = trimmed;
          if (trimmed && onApplyCreatePlanFields) {
            onApplyCreatePlanFields({ [ev.user_input.fd_key]: trimmed });
          }
        }
      }

      onResolved(key);
      try {
        await onOrchestratorResume?.(detail);
      } catch (resumeErr) {
        onError(
          resumeErr instanceof Error
            ? `The workflow could not resume: ${resumeErr.message}`
            : 'The workflow could not resume. Try the chat or refresh.'
        );
      }
    } catch {
      onError('Something went wrong. Please try again or send another message.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Stack spacing={1.5} sx={{ mb: 1 }}>
      {items.map(({ key, data: ev }) => (
        <Card key={key} elevation={0} sx={{ ...maestroSurfaceSx, p: 0, boxShadow: 'none' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brandColors.deepTeal }}>
                {titleFor(ev)}
              </Typography>
            </Stack>

            {ev.approval_phase === 'user_input' && ev.user_input ? (
              <Box sx={{ mt: 1 }}>
                {ev.user_input.context && (
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {ev.user_input.context}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {ev.user_input.question}
                </Typography>
                {isNumberInput(ev) ? (
                  <ApprovalNumberField
                    tenantId={tenantId}
                    fdKey={ev.user_input.fd_key || ev.user_input.field}
                    fallbackLabel={ev.user_input.field}
                    value={userInputNumbers[key] ?? null}
                    onChange={(amount) => setUserInputNumbers((v) => ({ ...v, [key]: amount }))}
                    disabled={!!busyKey || disabled}
                    currencyFieldValue={getFormFieldValue(PLAN_CURRENCY_FD_KEY)}
                  />
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    type={ev.user_input.field_type === 'date' ? 'date' : 'text'}
                    placeholder={
                      ev.user_input.field_type === 'date'
                        ? 'YYYY-MM-DD'
                        : `Enter ${ev.user_input.field}…`
                    }
                    value={userInputValues[key] || ''}
                    onChange={(e) => setUserInputValues((v) => ({ ...v, [key]: e.target.value }))}
                    disabled={!!busyKey || disabled}
                    InputLabelProps={
                      ev.user_input.field_type === 'date' ? { shrink: true } : undefined
                    }
                  />
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="flex-end">
                  <SecondaryButton
                    size="small"
                    disabled={!!busyKey || disabled}
                    onClick={() => handleDecision(key, ev, 'rejected')}
                  >
                    {busyKey === key ? '…' : 'Skip'}
                  </SecondaryButton>
                  <Button
                    size="small"
                    variant="contained"
                    data-testid="approval-user-input-submit-btn"
                    disabled={
                      !!busyKey ||
                      disabled ||
                      // ORCH-373: nothing entered means nothing to submit.
                      !canSubmitUserInput(userInputAnswer(key, ev))
                    }
                    onClick={() => handleDecision(key, ev, 'approved')}
                  >
                    {busyKey === key ? '…' : 'Submit'}
                  </Button>
                </Stack>
              </Box>
            ) : ev.approval_phase === 'mdm_mapping' && ev.mapping ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {labelFor(ev.mapping.fd_key, ev.mapping.field_label)}
                </Typography>
                {ev.mapping.explanation ? (
                  <Typography variant="body2" sx={{ mt: 0.75, mb: 1, color: 'text.secondary' }}>
                    {ev.mapping.explanation}
                  </Typography>
                ) : ev.detail ? (
                  <Typography variant="body2" sx={{ mt: 0.75, mb: 1, color: 'text.secondary' }}>
                    {ev.detail}
                  </Typography>
                ) : null}
                {ev.mapping.brief_term ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Brief reference: &quot;{ev.mapping.brief_term}&quot;
                  </Typography>
                ) : null}
                <ApprovalMappingField
                  tenantId={tenantId}
                  mapping={ev.mapping}
                  liveFormValue={getFormFieldValue(ev.mapping.fd_key)}
                  disabled={!!busyKey || disabled}
                  onApplyToPlanForm={(fields) => onApplyCreatePlanFields?.(fields)}
                  onResolvedChoice={(choice) =>
                    setMappingChoice((prev) => ({ ...prev, [key]: choice }))
                  }
                />
                {mappingHint[key] ? (
                  <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                    {mappingHint[key]}
                  </Alert>
                ) : null}
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="flex-end">
                  <SecondaryButton
                    size="small"
                    disabled={!!busyKey || disabled}
                    onClick={() => handleDecision(key, ev, 'rejected')}
                  >
                    {busyKey === key ? '…' : 'Skip'}
                  </SecondaryButton>
                  <Button
                    size="small"
                    variant="contained"
                    data-testid="approval-mapping-approve-btn"
                    disabled={
                      !!busyKey ||
                      disabled ||
                      // ORCH-373: nothing selected means nothing to approve.
                      !canConfirmMapping(getFormFieldValue(ev.mapping.fd_key), mappingChoice[key])
                    }
                    onClick={() => handleDecision(key, ev, 'approved')}
                  >
                    {busyKey === key ? '…' : 'Approve'}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Confidence: {(ev.confidence ?? 0).toFixed(2)}
                </Typography>
                {ev.detail ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {ev.detail}
                  </Typography>
                ) : null}
                {ev.issues?.length ? (
                  <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                    {ev.issues.map((issue, i) => (
                      <li key={i}>
                        <Typography variant="body2">{issue}</Typography>
                      </li>
                    ))}
                  </Box>
                ) : null}
                {ev.output_snapshot ? (
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      maxHeight: 120,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                    }}
                  >
                    {ev.output_snapshot}
                  </Typography>
                ) : null}
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Optional comment (e.g. rejection reason)"
                  value={comments[key] || ''}
                  onChange={(e) => setComments((c) => ({ ...c, [key]: e.target.value }))}
                  sx={{ mt: 1.5 }}
                  disabled={!!busyKey || disabled}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="flex-end">
                  <DestructiveButton
                    size="small"
                    disabled={!!busyKey || disabled}
                    onClick={() => handleDecision(key, ev, 'rejected')}
                  >
                    {busyKey === key ? '…' : 'Reject'}
                  </DestructiveButton>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!!busyKey || disabled}
                    onClick={() => handleDecision(key, ev, 'approved')}
                  >
                    {busyKey === key ? '…' : 'Approve'}
                  </Button>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default ApprovalCard;
